export function isRelativeUrl(url) {
  const absoluteUrlRegExp = /^(?:[a-z+]+:)?\/\//i
  return !absoluteUrlRegExp.test(url)
}

export function addQueryParamsToRedirectUrl(url, params) {
  const parsedUrl = new URL(url)

  for (const [key, value] of Object.entries(params)) {
    if (['language'].includes(key)) {
      parsedUrl.searchParams.append(key, value)
    }
  }

  if (params.original && isRelativeUrl(params.original)) {
    parsedUrl.searchParams.append('original', params.original)
  }

  if (params.invitationToken) {
    parsedUrl.searchParams.append(
      'workspace_invitation_token',
      params.invitationToken
    )
  }

  return parsedUrl.toString()
}

const SAFE_URL_PROTOCOLS = [
  'http:',
  'https:',
  'ftp:',
  'ftps:',
  'mailto:',
  'tel:',
  'sms:',
]

export function getUrlScheme(value) {
  // Strip chars browsers ignore, so `java\tscript:` still reads as `javascript:`.
  // eslint-disable-next-line no-control-regex
  const stripped = String(value ?? '').replace(/[\x00-\x20]/g, '')
  const match = stripped.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/)
  return match ? `${match[1].toLowerCase()}:` : null
}

export function ensureUrlProtocol(value) {
  const scheme = getUrlScheme(value)
  if (scheme && SAFE_URL_PROTOCOLS.includes(scheme)) {
    return value
  }
  // Protocol-relative URLs only need the scheme, not another `//`.
  if (String(value).startsWith('//')) {
    return `https:${value}`
  }
  // Force https so an unsafe scheme (javascript:, data:) can't execute as a URI.
  return `https://${value}`
}

/**
 * Parses a comma-separated list of URLs and extracts their hostnames.
 * @param {string} urlsString - Comma-separated list of URLs
 * @returns {string[]} Array of hostnames extracted from valid URLs
 */
export function parseHostnamesFromUrls(urlsString) {
  if (!urlsString) return []

  return urlsString
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url !== '')
    .map((url) => {
      try {
        return new URL(url).hostname
      } catch (e) {
        console.warn(`Invalid URL in BASEROW_EXTRA_PUBLIC_URLS: ${url}`)
        return null
      }
    })
    .filter((hostname) => hostname !== null)
}

/**
 * Returns a favicon image URL for the host of the given web URL, or null when
 * the value is empty or not an http(s) URL (so mailto:/tel: get no favicon).
 *
 * Resolves the icon from the domain, so no per-row upload is needed, and stays
 * cheap at scale: cells render it in a lazy-loaded <img>, so only on-screen rows
 * fetch, repeated domains come from the browser HTTP cache, and the icons are
 * tiny.
 *
 * KNOW WHAT THIS IS. `t2.gstatic.com/faviconV2` is Chrome-internal plumbing, not
 * a public Google API: there is no documentation, no terms of service and no
 * SLA for it, and Google's own extensions team describes the sibling
 * `s2/favicons` endpoint as "an exposed, yet undocumented/unofficial service
 * that may change or break at any moment". It can be rate-limited or withdrawn
 * without notice, and if it is, every URL cell silently loses its icon.
 *
 * It also means Google sees, per rendered row, the domain of the link (it is
 * the query parameter) and the viewer's IP. `referrerpolicy="no-referrer"` at
 * each call site keeps the deployment's own hostname out of it, but the domain
 * and the IP are inherent to using any third-party favicon service. For an
 * EU-hosted deployment that is the Google Fonts problem in a different costume
 * (LG München I, 3 O 17493/20), so a self-hosted resolver -- fetch and cache
 * server-side -- is the correct fix if this ever needs to be defensible rather
 * than merely convenient.
 *
 * @param {string} value - A URL, with or without protocol.
 * @param {number} size - Requested icon size in px.
 * @returns {string|null}
 */
export function getFaviconUrl(value, size = 32) {
  if (!value) return null
  try {
    const scheme = getUrlScheme(value)
    // Only build favicons for web links; skip mailto:/tel:/etc.
    if (scheme && !['http:', 'https:'].includes(scheme)) return null
    const url = new URL(ensureUrlProtocol(value))
    if (!url.hostname || !url.hostname.includes('.')) return null
    return `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(
      url.origin
    )}&size=${size}`
  } catch (e) {
    return null
  }
}
